import { isEqual } from "lodash";
import { observer } from "mobx-react-lite";
import { FC } from "react";
import { Field, useField, useForm } from "react-final-form";

import { newGuid, unique } from "@bps/common-utils";
import {
  dataAttribute,
  DataAttributes,
  HideStack,
  Link,
  MessageBar,
  Stack,
  Text,
  TextWithIcon,
  useTheme
} from "@bps/fluent-ui";
import {
  ContactType,
  EmploymentType,
  RelationshipType
} from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { Contact } from "@stores/practice/models/Contact.ts";
import { ContactFetcher } from "@ui-components/ContactFetcher.tsx";
import { AllMutators } from "@ui-components/form/customMutators.ts";
import { FieldCondition } from "@ui-components/form/FieldCondition.tsx";
import { FieldSpy } from "@ui-components/form/FieldSpy.tsx";
import { SubmissionFormDialog } from "@ui-components/form/submission-form-dialog/SubmissionFormDialog.tsx";
import { ToggleField } from "@ui-components/form/Toggle/ToggleField.tsx";
import { ContactPickerField } from "@ui-components/pickers/contact-picker/ContactPickerField.tsx";

import { EmployerFields } from "../EmployerFields.tsx";
import { EmployerInfoCard } from "../EmployerInfoCard.tsx";
import { toggleFieldStyle } from "../employers/Employers.styles.ts";
import {
  PatientEditFormValues,
  patientFormNameOf
} from "../PatientEditFormValues.tsx";

interface ChooseOrganisationDialogProps {
  onDismiss: () => void;
  contact: Contact;
}

export interface EmployersContactMethodsValues {
  employerId?: string;
  linkedOrganisationsIds?: string[];
  currentJob?: boolean;
  primaryEmployer?: boolean;
  employmentType?: EmploymentType;
  otherInformation?: string;
  saveAsEmployer?: boolean;
}

export const empContactMethodsNameOf =
  nameOfFactory<EmployersContactMethodsValues>();

export const ChooseOrganisationDialog: FC<ChooseOrganisationDialogProps> =
  observer(({ onDismiss, contact }) => {
    const { mutators } = useForm<PatientEditFormValues>();

    const theme = useTheme();

    const onSaveEmployersContactMethods = (
      values: EmployersContactMethodsValues,
      linkedOrganisationContactIds: string[] | undefined,
      mutators: AllMutators
    ) => {
      const newEmpContactMethods =
        values.linkedOrganisationsIds?.filter(
          x => !linkedOrganisationContactIds?.includes(x)
        ) ?? [];
      if (values.saveAsEmployer) {
        mutators.push(patientFormNameOf("employers"), {
          animationId: newGuid(),
          relatedContactId: values.employerId,
          relationship: RelationshipType.Employer,
          hasRelationship: true,
          metadata: {
            relationshipTypeCode: RelationshipType.Employer,
            employmentType: values.employmentType,
            otherInformation: values.otherInformation,
            currentJob: !!values.currentJob,
            primaryJob: !!values.primaryEmployer
          }
        });
      }
      if (
        values.employerId &&
        !linkedOrganisationContactIds?.includes(values.employerId)
      ) {
        mutators.push(
          patientFormNameOf("linkedOrganisationContactIds"),
          values.employerId
        );
      }

      if (newEmpContactMethods.length > 0) {
        newEmpContactMethods.map(x =>
          mutators.push(patientFormNameOf("linkedOrganisationContactIds"), x)
        );
      }
    };

    const {
      input: { value: linkedOrganisationContactIds }
    } = useField<PatientEditFormValues["linkedOrganisationContactIds"]>(
      patientFormNameOf("linkedOrganisationContactIds"),
      {
        subscription: { value: true }
      }
    );

    const onHandleDismiss = () => {
      onDismiss();
    };

    const onHandleConfirm = (values: EmployersContactMethodsValues) => {
      onSaveEmployersContactMethods(
        values,
        linkedOrganisationContactIds,
        mutators
      );
      onDismiss();
    };

    const initialValues: EmployersContactMethodsValues = {
      linkedOrganisationsIds: [...(linkedOrganisationContactIds ?? [])],
      employmentType: EmploymentType.Employee,
      otherInformation: "",
      employerId:
        contact.currentEmployers.length === 1
          ? contact.currentEmployers[0].id
          : undefined,
      saveAsEmployer: false
    };

    return (
      <SubmissionFormDialog<EmployersContactMethodsValues>
        dialogName="Contact methods from employers"
        initialValues={initialValues}
        buttonsProps={form => {
          const { values, initialValues } = form.getState();
          const linkedOrganisationsIdsNotChanged =
            (values.linkedOrganisationsIds ?? []).length > 1 &&
            isEqual(
              values.linkedOrganisationsIds,
              initialValues.linkedOrganisationsIds
            );

          return {
            submitButtonProps: {
              disabled:
                (!values.employerId && linkedOrganisationsIdsNotChanged) ||
                (!values.employerId &&
                  values.linkedOrganisationsIds?.length === 0),
              disableSubmitOnPristine: false,
              text: "Save",
              iconProps: {
                hidden: true
              }
            },
            cancelButtonProps: { text: "Cancel" }
          };
        }}
        dialogProps={{
          dialogContentProps: {
            title: "Get contact methods from an organisation"
          },
          onDismiss: onHandleDismiss,
          minWidth: 500,
          maxWidth: 500,
          styles: {
            root: {
              ".ms-Dialog-action": { width: "100%" }
            }
          }
        }}
        onSubmit={onHandleConfirm}
      >
        {({ values, form, initialValues }) => {
          const combinedArray = unique([
            ...(linkedOrganisationContactIds ?? []),
            ...contact.currentEmployers.map(x => x.id)
          ]);

          const hideEmployerToggle =
            (!values.employerId &&
              values.employerId === initialValues.employerId) ||
            (values.employerId &&
              linkedOrganisationContactIds?.includes(values.employerId)) ||
            contact.employerContacts.filter(x => x.id === values.employerId)
              .length > 0;

          return (
            <>
              <Stack tokens={{ childrenGap: 8 }}>
                {contact.currentEmployers.length === 0 &&
                  !values.employerId && (
                    <Text
                      styles={{
                        root: { color: theme.palette.neutralSecondary }
                      }}
                    >
                      No available contact methods, as {contact.fullName} is not
                      currently linked to an organisation
                    </Text>
                  )}
                <Stack tokens={{ childrenGap: 8 }}>
                  <ContactPickerField
                    name={empContactMethodsNameOf("employerId")}
                    label="Organisation"
                    filter={{
                      types: [ContactType.Organisation]
                    }}
                  />
                  <FieldSpy
                    name={empContactMethodsNameOf("employerId")}
                    onChange={(contactId: string | undefined) => {
                      if (contactId) {
                        if (
                          contactId !== initialValues.employerId &&
                          !contact.employerContacts
                            .map(x => x.id)
                            .includes(contactId)
                        ) {
                          form.change(
                            empContactMethodsNameOf("saveAsEmployer"),
                            true
                          );
                        }
                      } else {
                        form.change(
                          empContactMethodsNameOf("saveAsEmployer"),
                          false
                        );
                      }
                    }}
                  />
                  <FieldCondition
                    when={empContactMethodsNameOf("employerId")}
                    is={(employerId: string | undefined) =>
                      !!employerId || !!initialValues.employerId
                    }
                  >
                    <EmployerInfoCard
                      selectedOrgId={
                        values.employerId ?? initialValues.employerId
                      }
                    />
                  </FieldCondition>
                  {(combinedArray ?? []).length > 1 && !values.employerId && (
                    <MessageBar>
                      <Stack>
                        <Text
                          styles={{ root: { fontSize: "inherit" } }}
                        >{`${contact.name} is linked to multiple organisations; use`}</Text>

                        {(combinedArray ?? []).map(id => {
                          return (
                            <ContactFetcher contactId={id} key={id}>
                              {contact => {
                                return values.linkedOrganisationsIds?.includes(
                                  contact.id
                                ) ? (
                                  <TextWithIcon
                                    key={contact.id}
                                    iconProps={{
                                      iconName: "SkypeCheck"
                                    }}
                                    styles={{
                                      root: { fontSize: "inherit" }
                                    }}
                                  >
                                    {contact.name}
                                  </TextWithIcon>
                                ) : (
                                  <Link
                                    onClick={() => {
                                      form.mutators.push(
                                        "linkedOrganisationsIds",
                                        contact.id
                                      );
                                    }}
                                  >
                                    {contact.name}
                                  </Link>
                                );
                              }}
                            </ContactFetcher>
                          );
                        })}
                      </Stack>
                    </MessageBar>
                  )}
                </Stack>
              </Stack>
              <Stack tokens={{ childrenGap: 8 }}>
                <HideStack when={hideEmployerToggle}>
                  <ToggleField
                    {...dataAttribute(
                      DataAttributes.Element,
                      "save-as-employer-toggle"
                    )}
                    name={empContactMethodsNameOf("saveAsEmployer")}
                    label="Save as employer"
                    onText="Yes"
                    offText="No"
                    styles={toggleFieldStyle}
                  />
                </HideStack>
                <FieldCondition
                  when={empContactMethodsNameOf("saveAsEmployer")}
                  is={true}
                >
                  <EmployerFields />
                </FieldCondition>
                <Field
                  name={empContactMethodsNameOf("linkedOrganisationsIds")}
                  component={() => null}
                />
              </Stack>
            </>
          );
        }}
      </SubmissionFormDialog>
    );
  });
